import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { Site } from '../lib/api'
import { api } from '../lib/api'
import { Modal, Button, Field, Input, Select, Badge, ErrorBox } from './ui'
import { RowMenu } from './RowMenu'

interface Redirect { id: number; from_path: string; to_url: string; code: number }
interface ErrorPage { id: number; code: number; path: string }

export function SiteExtrasModal({ site, onClose, onTask }: {
  site: Site; onClose: () => void; onTask?: (id: string) => void
}) {
  const [tab, setTab] = useState<'redir' | 'err'>('redir')
  return (
    <Modal open onClose={onClose} title={`Дополнительно: ${site.domain}`} size="lg">
      <div className="flex gap-2 mb-4 border-b border-neutral-100">
        <TabBtn active={tab === 'redir'} onClick={() => setTab('redir')}>Редиректы</TabBtn>
        <TabBtn active={tab === 'err'} onClick={() => setTab('err')}>Страницы ошибок</TabBtn>
      </div>
      {tab === 'redir' ? <RedirectsTab siteId={site.id} onTask={onTask} /> : <ErrorPagesTab siteId={site.id} onTask={onTask} />}
      <div className="flex justify-end pt-3 border-t border-neutral-100 mt-4">
        <Button variant="ghost" onClick={onClose}>Закрыть</Button>
      </div>
    </Modal>
  )
}

function TabBtn({ active, onClick, children }: any) {
  return (
    <button onClick={onClick}
      className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition ${active ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>
      {children}
    </button>
  )
}

function RedirectsTab({ siteId, onTask }: { siteId: number; onTask?: (id: string) => void }) {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [err, setErr] = useState('')

  const { data = [], isLoading } = useQuery({
    queryKey: ['site-redirects', siteId],
    queryFn: async () => (await api.get<Redirect[]>(`/sites/${siteId}/redirects`)).data,
  })
  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/sites/${siteId}/redirects/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-redirects', siteId] }),
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] text-neutral-500">Правила <code>rewrite ^{'{from}'}$ {'{to}'} redirect;</code> в vhost.</div>
        <Button size="sm" onClick={() => setAddOpen(true)}>+ Добавить</Button>
      </div>
      {err && <ErrorBox>{err}</ErrorBox>}
      {isLoading && <div className="text-neutral-400 text-[12px]">Загрузка…</div>}
      {!isLoading && data.length === 0 && <div className="text-center text-neutral-400 text-[13px] py-8">Редиректов нет</div>}
      {data.length > 0 && (
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
            <tr>
              <th className="text-left font-medium px-3 py-2">Откуда</th>
              <th className="text-left font-medium px-3 py-2">Куда</th>
              <th className="text-left font-medium px-3 py-2 w-[80px]">Код</th>
              <th className="w-[40px]"></th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                <td className="px-3 py-2 font-mono text-[12px]">{r.from_path}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-neutral-700">{r.to_url}</td>
                <td className="px-3 py-2"><Badge>{r.code}</Badge></td>
                <td className="px-3 py-2 text-right">
                  <RowMenu items={[{ label: 'Удалить', danger: true,
                    onClick: () => confirm('Удалить редирект?') && del.mutate(r.id) }]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {addOpen && <AddRedirectModal siteId={siteId}
        onClose={() => setAddOpen(false)}
        onDone={(taskId) => {
          setAddOpen(false)
          qc.invalidateQueries({ queryKey: ['site-redirects', siteId] })
          if (taskId && onTask) onTask(taskId)
        }}
        onError={setErr}
      />}
    </div>
  )
}

function AddRedirectModal({ siteId, onClose, onDone, onError }: {
  siteId: number; onClose: () => void; onDone: (taskId?: string) => void; onError: (m: string) => void
}) {
  const [f, setF] = useState({ from_path: '/', to_url: '', code: 301 })
  const add = useMutation({
    mutationFn: async () => (await api.post(`/sites/${siteId}/redirects`, f)).data,
    onSuccess: (r: any) => onDone(r.task_id),
    onError: (e: any) => onError(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Новый редирект">
      <form onSubmit={(e) => { e.preventDefault(); add.mutate() }} className="space-y-3">
        <Field label="Откуда (regex или путь)" hint="Например: /old-page или ^/blog/(.*)$">
          <Input value={f.from_path} onChange={e => setF({ ...f, from_path: e.target.value })} required autoFocus className="font-mono" />
        </Field>
        <Field label="Куда (URL или путь)" hint="Например: https://newsite.com/$1 или /new-page">
          <Input value={f.to_url} onChange={e => setF({ ...f, to_url: e.target.value })} required className="font-mono" />
        </Field>
        <Field label="Код">
          <Select value={f.code} onChange={e => setF({ ...f, code: +e.target.value })}>
            <option value={301}>301 — постоянный</option>
            <option value={302}>302 — временный</option>
            <option value={307}>307 — временный (сохранить метод)</option>
            <option value={308}>308 — постоянный (сохранить метод)</option>
          </Select>
        </Field>
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={add.isPending}>{add.isPending ? '…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}

function ErrorPagesTab({ siteId, onTask }: { siteId: number; onTask?: (id: string) => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ code: 404, path: '/errors/404.html' })
  const [err, setErr] = useState('')

  const { data = [], isLoading } = useQuery({
    queryKey: ['site-errors', siteId],
    queryFn: async () => (await api.get<ErrorPage[]>(`/sites/${siteId}/error-pages`)).data,
  })
  const upsert = useMutation({
    mutationFn: async () => (await api.post(`/sites/${siteId}/error-pages`, f)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['site-errors', siteId] })
      if (r?.task_id && onTask) onTask(r.task_id)
      setF({ code: 404, path: '/errors/404.html' })
    },
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/sites/${siteId}/error-pages/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-errors', siteId] }),
  })

  return (
    <div>
      <div className="text-[12px] text-neutral-500 mb-3">Директива <code>error_page {'{code}'} {'{path}'};</code> в vhost. Файл должен существовать в корне сайта.</div>
      {err && <ErrorBox>{err}</ErrorBox>}
      {isLoading && <div className="text-neutral-400 text-[12px]">Загрузка…</div>}
      {!isLoading && data.length === 0 && <div className="text-center text-neutral-400 text-[13px] py-4">Пока пусто</div>}
      {data.length > 0 && (
        <table className="w-full text-[13px] mb-3">
          <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
            <tr>
              <th className="text-left font-medium px-3 py-2 w-[80px]">Код</th>
              <th className="text-left font-medium px-3 py-2">Путь</th>
              <th className="w-[40px]"></th>
            </tr>
          </thead>
          <tbody>
            {data.map(e => (
              <tr key={e.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                <td className="px-3 py-2"><Badge tone={e.code >= 500 ? 'red' : 'yellow'}>{e.code}</Badge></td>
                <td className="px-3 py-2 font-mono text-[12px]">{e.path}</td>
                <td className="px-3 py-2 text-right">
                  <RowMenu items={[{ label: 'Удалить', danger: true, onClick: () => del.mutate(e.id) }]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form onSubmit={(e) => { e.preventDefault(); upsert.mutate() }}
            className="flex items-end gap-2 bg-neutral-50/60 p-3 rounded-md border border-neutral-100">
        <Field label="Код" className="w-[100px]">
          <Select value={f.code} onChange={e => setF({ ...f, code: +e.target.value })}>
            {[400, 401, 403, 404, 500, 502, 503, 504].map(c =>
              <option key={c} value={c}>{c}</option>
            )}
          </Select>
        </Field>
        <Field label="Путь (относительно docroot)" className="flex-1">
          <Input value={f.path} onChange={e => setF({ ...f, path: e.target.value })} className="font-mono" placeholder="/errors/404.html" required />
        </Field>
        <Button type="submit" disabled={upsert.isPending}>{upsert.isPending ? '…' : 'Добавить'}</Button>
      </form>
    </div>
  )
}
